import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface AssistantSettings {
  assistant_name: string;
  assistant_avatar: string;
}

export const ASSISTANT_AVATARS = [
  { id: "robot-1", emoji: "🤖", label: "Robot Clásico" },
  { id: "helmet-neon", emoji: "⛑️", label: "Casco Neón" },
  { id: "wrench-bot", emoji: "🔧", label: "Mecánico Virtual" },
  { id: "racing-ai", emoji: "🏎️", label: "Piloto IA" },
  { id: "cyber-moto", emoji: "🏍️", label: "Cyber Moto" },
] as const;

const DEFAULT_SETTINGS: AssistantSettings = {
  assistant_name: "Pit-Stop",
  assistant_avatar: "robot-1",
};

export function useAssistantSettings() {
  const [settings, setSettings] = useState<AssistantSettings>(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("assistant_settings")
        .select("assistant_name, assistant_avatar")
        .eq("user_id", user.id)
        .single();

      if (data && !error) {
        setSettings({
          assistant_name: data.assistant_name,
          assistant_avatar: data.assistant_avatar,
        });
      }
    } catch {
      // Use defaults
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const saveSettings = useCallback(async (newSettings: AssistantSettings) => {
    setIsSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const { error } = await supabase
        .from("assistant_settings")
        .upsert(
          {
            user_id: user.id,
            assistant_name: newSettings.assistant_name.trim() || DEFAULT_SETTINGS.assistant_name,
            assistant_avatar: newSettings.assistant_avatar,
          },
          { onConflict: "user_id" }
        );

      if (error) throw error;

      setSettings(newSettings);
      toast.success("¡Asistente personalizado! 🤖");
      return true;
    } catch (error) {
      console.error("Error saving assistant settings:", error);
      toast.error("Error al guardar ajustes del asistente");
      return false;
    } finally {
      setIsSaving(false);
    }
  }, []);

  const getAvatarEmoji = useCallback((avatarId: string) => {
    return ASSISTANT_AVATARS.find((a) => a.id === avatarId)?.emoji || "🤖";
  }, []);

  return { settings, isLoading, isSaving, saveSettings, getAvatarEmoji };
}
